/**
 * Contextual local tick label formatting for timeseries linear axes.
 *
 * Labels use `Intl.DateTimeFormat` so date/time component order follows the
 * active locale. Compact vs extended detail follows Plotly-style boundary rules.
 *
 * @packageDocumentation
 * @module TimeseriesTickLabelFormat
 * @public
 */

import {
  TimeseriesTickGranularity,
  TimeseriesTickLabelDetail,
} from './timeseriesAxisTicks';

// -- type definitions --

/** Local wall-clock parts extracted in the display timezone. */
export interface TimeseriesLocalCalendarParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
  readonly millisecond: number;
}

/** Input for resolving compact vs extended label detail. */
export interface ResolveTickLabelDetailInput {
  readonly granularity: TimeseriesTickGranularity;
  readonly isFirstTick: boolean;
  readonly previousParts?: TimeseriesLocalCalendarParts;
  readonly currentParts: TimeseriesLocalCalendarParts;
}

/** Input for formatting one tick label. */
export interface FormatTickLabelInput {
  readonly epochMs: number;
  readonly displayTimezone: string;
  readonly granularity: TimeseriesTickGranularity;
  readonly detail: TimeseriesTickLabelDetail;
  readonly locale?: string | string[];
}

type TimeseriesCalendarField = keyof TimeseriesLocalCalendarParts;

// -- constants --

const MS = 1;
const SECOND_MS = 1000 * MS;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const MONTH_MS = 30 * DAY_MS;
const YEAR_MS = 365 * DAY_MS;

const GRANULARITY_TO_BOUNDARY_FIELD: Record<
  TimeseriesTickGranularity,
  TimeseriesCalendarField | undefined
> = {
  millisecond: 'second',
  second: 'minute',
  minute: 'hour',
  hour: 'day',
  day: 'month',
  month: 'year',
  year: undefined,
};

// -- internal helpers --

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function parseIntlPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): number {
  const raw = parts.find((part) => part.type === type)?.value;
  return raw !== undefined ? Number.parseInt(raw, 10) : 0;
}

function parseFractionalSecond(parts: Intl.DateTimeFormatPart[]): number {
  const raw = parts.find(
    (part) => part.type === ('fractionalSecond' as Intl.DateTimeFormatPartTypes)
  )?.value;
  if (!raw) {
    return 0;
  }

  return Number.parseInt(raw.padEnd(3, '0').slice(0, 3), 10);
}

/**
 * Maps tick step duration to the active label granularity bucket.
 *
 * @param stepMs - Selected tick step in milliseconds.
 * @returns Granularity used for contextual formatting.
 */
function resolveGranularityFromStepMsInternal(
  stepMs: number
): TimeseriesTickGranularity {
  if (stepMs < SECOND_MS) {
    return 'millisecond';
  }
  if (stepMs < MINUTE_MS) {
    return 'second';
  }
  if (stepMs < HOUR_MS) {
    return 'minute';
  }
  if (stepMs < DAY_MS) {
    return 'hour';
  }
  if (stepMs < MONTH_MS) {
    return 'day';
  }
  if (stepMs < YEAR_MS) {
    return 'month';
  }
  return 'year';
}

/**
 * Returns `Intl.DateTimeFormat` options for one granularity and detail level.
 *
 * Field inclusion follows the contextual tick spec; ordering is delegated to
 * `Intl` via the supplied locale (no manual date-part concatenation).
 *
 * @param granularity - Active tick label granularity.
 * @param detail - Compact or extended contextual detail.
 * @returns Options passed to `Intl.DateTimeFormat` with `timeZone` set by caller.
 */
function getTickLabelIntlOptions(
  granularity: TimeseriesTickGranularity,
  detail: TimeseriesTickLabelDetail
): Intl.DateTimeFormatOptions {
  const isExtended = detail === 'extended';

  switch (granularity) {
    case 'year':
      return { year: 'numeric' };
    case 'month':
      return isExtended
        ? { month: 'short', year: 'numeric' }
        : { month: 'short' };
    case 'day':
      return isExtended
        ? { day: 'numeric', month: 'short', year: 'numeric' }
        : { day: 'numeric', month: 'short' };
    case 'hour':
    case 'minute':
      return {
        ...(isExtended
          ? { day: 'numeric', month: 'short', year: 'numeric' }
          : {}),
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      };
    case 'second':
      return {
        ...(isExtended
          ? { day: 'numeric', month: 'short', year: 'numeric' }
          : {}),
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      };
    case 'millisecond':
      return {
        ...(isExtended
          ? { day: 'numeric', month: 'short', year: 'numeric' }
          : {}),
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
        hourCycle: 'h23',
      } as Intl.DateTimeFormatOptions;
  }
}

/**
 * Formats one instant with locale-aware ordering via `Intl.DateTimeFormat`.
 *
 * @param epochMs - Absolute instant in epoch milliseconds.
 * @param displayTimezone - IANA timezone used for local display.
 * @param locale - BCP 47 locale tag(s).
 * @param options - Date/time fields to include; order follows locale rules.
 * @returns Formatted local label without UTC offset suffix.
 */
function formatInstantWithIntl(
  epochMs: number,
  displayTimezone: string,
  locale: string | string[] | undefined,
  options: Intl.DateTimeFormatOptions
): string {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: displayTimezone,
  }).format(new Date(epochMs));
}

/**
 * Extracts local calendar parts for boundary and DST ambiguity checks.
 *
 * @param epochMs - Absolute instant in epoch milliseconds.
 * @param displayTimezone - IANA timezone used for local display.
 * @returns Normalized local calendar parts.
 */
function getLocalCalendarPartsInternal(
  epochMs: number,
  displayTimezone: string
): TimeseriesLocalCalendarParts {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: displayTimezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
    hourCycle: 'h23',
  } as Intl.DateTimeFormatOptions).formatToParts(new Date(epochMs));

  return {
    year: parseIntlPart(parts, 'year'),
    month: parseIntlPart(parts, 'month'),
    day: parseIntlPart(parts, 'day'),
    hour: parseIntlPart(parts, 'hour'),
    minute: parseIntlPart(parts, 'minute'),
    second: parseIntlPart(parts, 'second'),
    millisecond: parseFractionalSecond(parts),
  };
}

/**
 * Returns whether the current tick crosses the coarser calendar boundary.
 *
 * @param granularity - Active tick label granularity.
 * @param previousParts - Local parts of the preceding tick.
 * @param currentParts - Local parts of the current tick.
 * @returns Whether extended formatting should be considered.
 */
function crossesCoarserBoundary(
  granularity: TimeseriesTickGranularity,
  previousParts: TimeseriesLocalCalendarParts,
  currentParts: TimeseriesLocalCalendarParts
): boolean {
  const boundaryField = GRANULARITY_TO_BOUNDARY_FIELD[granularity];
  if (!boundaryField) {
    return false;
  }

  return previousParts[boundaryField] !== currentParts[boundaryField];
}

// -- exported API --

/**
 * Builds the ambiguity key for DST suffix rules at a granularity.
 *
 * @param parts - Local calendar parts in the display timezone.
 * @param granularity - Active tick label granularity.
 * @returns Key such as `HH:mm` or `HH:mm:ss`, or empty when not applicable.
 */
export function buildAmbiguityTimeKey(
  parts: TimeseriesLocalCalendarParts,
  granularity: TimeseriesTickGranularity
): string {
  switch (granularity) {
    case 'millisecond':
    case 'second':
      return `${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`;
    case 'minute':
    case 'hour':
      return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
    default:
      return '';
  }
}

/**
 * Maps tick step duration to the active label granularity bucket.
 *
 * @param stepMs - Selected tick step in milliseconds.
 * @returns Granularity used for contextual formatting.
 */
export function resolveGranularityFromStepMs(
  stepMs: number
): TimeseriesTickGranularity {
  return resolveGranularityFromStepMsInternal(stepMs);
}

/**
 * Extracts local calendar parts for one instant in the display timezone.
 *
 * Used by {@link buildLocalLabelIndex} in `timeseriesAxisTicks.ts`.
 *
 * @param epochMs - Absolute instant in epoch milliseconds.
 * @param displayTimezone - IANA timezone used for local display.
 * @returns Normalized local calendar parts.
 */
export function getLocalCalendarParts(
  epochMs: number,
  displayTimezone: string
): TimeseriesLocalCalendarParts {
  return getLocalCalendarPartsInternal(epochMs, displayTimezone);
}

/**
 * Resolves whether a tick uses compact or extended label detail.
 *
 * @param input - Granularity, tick position, and local calendar parts.
 * @returns Label detail level.
 */
export function resolveTickLabelDetail(
  input: ResolveTickLabelDetailInput
): TimeseriesTickLabelDetail {
  if (input.granularity === 'year') {
    return 'compact';
  }

  if (input.isFirstTick) {
    return 'extended';
  }

  if (
    input.previousParts &&
    crossesCoarserBoundary(
      input.granularity,
      input.previousParts,
      input.currentParts
    )
  ) {
    return 'extended';
  }

  return 'compact';
}

/** Input for formatting a timeseries hover timestamp. */
export interface FormatTimeseriesHoverLabelInput {
  readonly epochMs: number;
  readonly displayTimezone: string;
  readonly locale?: string | string[];
}

/**
 * Formats a hover timestamp with short full date and full time to milliseconds.
 *
 * Unlike axis ticks, hover labels always use extended millisecond detail regardless
 * of the visible zoom range.
 *
 * @param input - Instant, timezone, and locale.
 * @returns Local wall-clock label such as `7 Jul 2026, 12:30:45.123`.
 */
export function formatTimeseriesHoverLabel(
  input: FormatTimeseriesHoverLabelInput
): string {
  return formatTickLabel({
    epochMs: input.epochMs,
    displayTimezone: input.displayTimezone,
    granularity: 'millisecond',
    detail: 'extended',
    locale: input.locale,
  });
}

/**
 * Formats one contextual tick label with locale-correct component ordering.
 *
 * @param input - Instant, timezone, granularity, detail, and locale.
 * @returns Local wall-clock label without UTC offset suffix.
 */
export function formatTickLabel(input: FormatTickLabelInput): string {
  const options = getTickLabelIntlOptions(input.granularity, input.detail);
  return formatInstantWithIntl(
    input.epochMs,
    input.displayTimezone,
    input.locale,
    options
  );
}
